import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './Hero.css';

// Import the background image
const backgroundImage = '/back2.png';

interface HeroProps {
  onOpenWallet?: () => void;
}

const Hero: React.FC<HeroProps> = ({ onOpenWallet }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  // Initial values
  const initialUnderlyingAssets = 1018367827.50;
  const initialDistributedInterest = 11828875.18;
  const baseAPY = 4.20;

  const [stats, setStats] = useState([
    {
      value: '$11,827,110.61',
      label: '',
      description: 'Total Distributed Interest',
      change: '+2.3%',
      changeType: 'positive' as 'positive' | 'negative' | 'neutral',
      highlight: false
    },
    {
      value: '$18,271,851.27',
      label: '',
      description: 'Total Value Of Underlying Assets',
      change: '+1.8%',
      changeType: 'positive' as 'positive' | 'negative' | 'neutral',
      highlight: false
    },
    {
      value: '3.91%',
      label: '',
      description: '30d Avg. APY after fees',
      change: '+0.5%',
      changeType: 'positive' as 'positive' | 'negative' | 'neutral',
      highlight: false
    }
  ]);


  const [dailyAPY] = useState(() => {
    // Try to get stored APY from localStorage, or calculate new one
    const stored = localStorage.getItem('dailyAPY');
    const storedDate = localStorage.getItem('dailyAPYDate');
    const today = new Date().toDateString();

    if (stored && storedDate === today) {
      return parseFloat(stored);
    } else {
      // Calculate new daily APY variation
      const variationRange = Math.random() * 0.08 + 0.05; // 5-8%
      const variationDirection = Math.random() > 0.5 ? 1 : -1;
      const newAPY = baseAPY * (1 + variationDirection * variationRange);

      // Store in localStorage
      localStorage.setItem('dailyAPY', newAPY.toString());
      localStorage.setItem('dailyAPYDate', today);

      return newAPY;
    }
  });

  // Update stats every second
  useEffect(() => {
    const updateStats = () => {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const elapsedMs = now.getTime() - startOfDay;
      const elapsedDays = elapsedMs / (1000 * 60 * 60 * 24);

      // Underlying assets grow at 11% per month
      const monthlyGrowthRate = 0.11;
      const dailyGrowthRate = Math.pow(1 + monthlyGrowthRate, 1/30) - 1;
      const underlyingAssets = initialUnderlyingAssets * Math.pow(1 + dailyGrowthRate, elapsedDays);

      // Use the daily APY (changes once per day)
      const currentAPY = dailyAPY;

      // Distributed interest based on current APY and underlying assets
      const annualInterest = underlyingAssets * (currentAPY / 100);
      const dailyInterest = annualInterest / 365;
      const distributedInterest = initialDistributedInterest + (dailyInterest * elapsedDays);

      // Calculate real percentage changes
      const dailyAssetGrowth = ((underlyingAssets - initialUnderlyingAssets) / initialUnderlyingAssets) * 100;
      const dailyInterestGrowth = ((distributedInterest - initialDistributedInterest) / initialDistributedInterest) * 100;
      const apyChange = ((currentAPY - baseAPY) / baseAPY) * 100;

      setStats([
        {
          value: `$${distributedInterest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          label: '',
          description: 'Total Distributed Interest',
          change: `${dailyInterestGrowth >= 0 ? '+' : ''}${dailyInterestGrowth.toFixed(1)}%`,
          changeType: dailyInterestGrowth >= 0 ? 'positive' as 'positive' | 'negative' | 'neutral' : 'negative' as 'positive' | 'negative' | 'neutral',
          highlight: false
        },
        {
          value: `$${underlyingAssets.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          label: '',
          description: 'Total Value Of Underlying Assets',
          change: `${dailyAssetGrowth >= 0 ? '+' : ''}${dailyAssetGrowth.toFixed(1)}%`,
          changeType: dailyAssetGrowth >= 0 ? 'positive' as 'positive' | 'negative' | 'neutral' : 'negative' as 'positive' | 'negative' | 'neutral',
          highlight: false
        },
        {
          value: `${currentAPY.toFixed(2)}%`,
          label: '',
          description: '30d Avg. APY after fees',
          change: `${apyChange >= 0 ? '+' : ''}${apyChange.toFixed(1)}%`,
          changeType: apyChange >= 0 ? 'positive' as 'positive' | 'negative' | 'neutral' : 'negative' as 'positive' | 'negative' | 'neutral',
          highlight: false
        }
      ]);
    };

    // Initial update
    updateStats();

    // Set up interval to update every second
    const interval = setInterval(updateStats, 1000);

    return () => clearInterval(interval);
  }, [dailyAPY]);


  return (
    <section id="hero" className="hero">
      <div className="hero-background">
        <div
          className="hero-image"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        <div className="hero-overlay"></div>
      </div>

      <div className="container hero-content">
        <motion.div
          className="hero-text"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
      

          <motion.h1 className="hero-title" variants={itemVariants}>
            <br />Make the most of your&nbsp;
            <span className="title-gradient">USD.P</span>
          </motion.h1>

          <motion.h1 className="hero-subtitle" variants={itemVariants}>
            U.S. Treasury Bill-collateralized stablecoin for
            consistent pegging and portfolio diversification.
          </motion.h1>

          <motion.div className="hero-actions" variants={itemVariants}>
            <motion.button
              className="btn btn-primary btn-large"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const swapSection = document.querySelector('.swap-interface');
                if (swapSection) {
                  swapSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              <span>Buy USD.P</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.button>
            
            <motion.button
              className="btn btn-ghost btn-large"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const vaultsSection = document.querySelector('.vaults');
                if (vaultsSection) {
                  vaultsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M8 5V19L19 12L8 5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Staking</span>
            </motion.button>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-stats"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="metric-card"
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
            >
              <div className="metric-header">
                <span className="metric-label">{stat.description}</span>
              </div>
              <div className="metric-value">{stat.value}</div>
              <div className="metric-footer">
                <span className={`metric-change ${stat.changeType}`}>
                  {stat.change}
                </span>
                <span className="metric-description">24h change</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
      
    </section>
  );
};

export default Hero;